import { Grid, Text } from '@mantine/core';
import { type ArrayPath, type UseFormReturn, useFieldArray } from 'react-hook-form';
import { Show } from '../../../../../../../react-commons/components/control-flow/Show';
import { Form_RichText } from '../../../../../../../react-commons/components/form/inputs/Form_RichText';
import type { FormNames } from '../../../../../../../react-commons/utils/form/getFormNames';
import { Inputs } from '../../../../../../../react-commons/utils/form/inputs';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    RolePropertyValidation,
    type RoleResource,
} from '../../../../../resource/resources/artifacts/role/roleResource';
import { Form_PropertyFieldArrayString } from '../../../../reusable-components/base/property-bases/Form_PropertyFieldArrayString';
import { From_PropertyColorPicker } from '../../../../reusable-components/base/property-bases/From_PropertyColorPicker';
import { PropertyInputs } from '../../../../reusable-components/base/property-bases/PropertyInputs';
import { CrudValidationTable } from '../../../../reusable-components/tables/CrudValidationTable';
import type { PropertyValidationInformation } from '../../../../util/PropertyValidationInformation';

export const RoleContent = ({
    id,
    form,
    formNames,
    readOnly,
    displayedInOverview,
}: {
    id?: string;
    form: UseFormReturn<RoleResource>;
    formNames: FormNames<RoleResource>;
    readOnly: boolean;
    displayedInOverview?: boolean;
}) => {
    const getPropertyValidationInformation = (
        propertyIdentifier: RolePropertyValidation
    ): PropertyValidationInformation => ({
        resourceId: id,
        resourceType: ResourceType.ROLE,
        propertyIdentifier,
    });

    const {
        fields: taskFields,
        append: appendTask,
        remove: removeTask,
        move: moveTask,
    } = useFieldArray({
        control: form.control,
        name: formNames.tasks as ArrayPath<RoleResource>,
    });

    const {
        fields: authorityFields,
        append: appendAuthority,
        remove: removeAuthority,
        move: moveAuthority,
    } = useFieldArray({
        control: form.control,
        name: formNames.authorities as ArrayPath<RoleResource>,
    });

    const {
        fields: trainingFields,
        append: appendTraining,
        remove: removeTraining,
        move: moveTraining,
    } = useFieldArray({
        control: form.control,
        name: formNames.trainings as ArrayPath<RoleResource>,
    });

    return (
        <Grid>
            <Show when={!displayedInOverview}>
                <Grid.Col span={8}>
                    <PropertyInputs.Name
                        name={formNames.name}
                        control={form.control}
                        readOnly={readOnly}
                        propertyValidationInformation={getPropertyValidationInformation(RolePropertyValidation.NAME)}
                    />
                </Grid.Col>
                <Grid.Col span={4}>
                    <From_PropertyColorPicker
                        name={formNames.color}
                        control={form.control}
                        readOnly={readOnly}
                        nullable={false}
                        propertyValidationInformation={getPropertyValidationInformation(RolePropertyValidation.COLOR)}
                    />
                </Grid.Col>
            </Show>
            <Grid.Col span={12}>
                <Text>Description</Text>
                <Form_RichText name={formNames.description} form={form} readonly={readOnly} />
            </Grid.Col>
            <Grid.Col span={12}>
                {readOnly ?
                    <Form_PropertyFieldArrayString
                        name={formNames.tasks}
                        control={form.control}
                        readOnly={readOnly}
                        label={'Tasks and Responsibilities'}
                        notCollapsible={true}
                        parentId={id}
                    />
                :   <CrudValidationTable
                        columns={[
                            {
                                accessorKey: `id`,
                                type: 'string',
                                header: 'Value',
                                Header: 'Value',
                                Cell: ({ row: { index } }) => (
                                    <Inputs.String
                                        control={form.control}
                                        nullable={true}
                                        readOnly={false}
                                        name={`${formNames.tasks}.${index}`}
                                        w={'100%'}
                                    />
                                ),
                            },
                        ]}
                        data={taskFields}
                        label={'Tasks and Responsibilities'}
                        onAdd={() => appendTask('')}
                        onDelete={(_, index) => removeTask(index)}
                        onIndexChange={(_, oldIndex, newIndex) => moveTask(oldIndex, newIndex)}
                        onEdit={undefined}
                    />
                }
            </Grid.Col>
            <Grid.Col span={12}>
                {readOnly ?
                    <Form_PropertyFieldArrayString
                        name={formNames.authorities}
                        control={form.control}
                        readOnly={readOnly}
                        label={'Authorities'}
                        notCollapsible={true}
                        parentId={id}
                    />
                :   <CrudValidationTable
                        columns={[
                            {
                                accessorKey: `id`,
                                type: 'string',
                                header: 'Value',
                                Header: 'Value',
                                Cell: ({ row: { index } }) => (
                                    <Inputs.String
                                        control={form.control}
                                        nullable={true}
                                        readOnly={false}
                                        name={`${formNames.authorities}.${index}`}
                                        w={'100%'}
                                    />
                                ),
                            },
                        ]}
                        data={authorityFields}
                        label={'Authorities'}
                        onAdd={() => appendAuthority('')}
                        onDelete={(_, index) => removeAuthority(index)}
                        onIndexChange={(_, oldIndex, newIndex) => moveAuthority(oldIndex, newIndex)}
                        onEdit={undefined}
                    />
                }
            </Grid.Col>
            <Grid.Col span={12}>
                {readOnly ?
                    <Form_PropertyFieldArrayString
                        name={formNames.trainings}
                        control={form.control}
                        readOnly={readOnly}
                        label={'Trainings'}
                        notCollapsible={true}
                        parentId={id}
                    />
                :   <CrudValidationTable
                        columns={[
                            {
                                accessorKey: `id`,
                                type: 'string',
                                header: 'Value',
                                Header: 'Value',
                                Cell: ({ row: { index } }) => (
                                    <Inputs.String
                                        control={form.control}
                                        nullable={true}
                                        readOnly={false}
                                        name={`${formNames.trainings}.${index}`}
                                        w={'100%'}
                                    />
                                ),
                            },
                        ]}
                        data={trainingFields}
                        label={'Trainings'}
                        onAdd={() => appendTraining('')}
                        onDelete={(_, index) => removeTraining(index)}
                        onIndexChange={(_, oldIndex, newIndex) => moveTraining(oldIndex, newIndex)}
                        onEdit={undefined}
                    />
                }
            </Grid.Col>
        </Grid>
    );
};
